import type { RiskAssessment } from '@nestjs-boilerplate/types';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ChevronLeft, Download, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import logoImage from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { riskAssessmentKeys } from '@/features/dashboard/api/risk-assessments';
import { deductBalance, SERVICES, useCurrentBalance } from '@/features/payments';
import { AddFundsModal } from '@/features/settings/components/add-funds-modal';
import { UserMenu } from '@/layouts/components/user-menu';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  downloadAllEvidence,
  downloadEvidence,
  downloadFullReportPdf,
  downloadSraForm,
  upgradeToEdd,
  useAssessmentReport,
  useScreeningStatus,
  wizardKeys,
} from '../api/risk-assessment-wizard';
import { type AdverseMediaEvidenceItem, AdverseMediaSection } from './adverse-media-section';
import { AuditTrailModal } from './audit-trail-modal';
import { BasicReportPage } from './basic-report-page';
import { BusinessRiskSection } from './business-risk-section';
import { EddModal } from './edd-modal';
import { EddUpsellModal } from './edd-upsell-modal';
import { EvidencePackageSection, getEvidenceFiles } from './evidence-package-section';
import { MatchReportModal } from './match-report-modal';
import { NoReportAccessModal } from './no-report-access-modal';
import { ReportClientDetails } from './report-client-details';
import { ScreeningLogSection } from './screening-log-section';
import { ScreeningModal } from './screening-modal';
import { SummaryOfFindingsSection } from './summary-of-findings-section';

interface ReportPageProps {
  assessmentId: string;
}

type RiskLevel = 'LOW' | 'MEDIUM' | 'HIGH';

interface ScreeningError {
  check:
    | 'uk_sanctions'
    | 'eu_sanctions'
    | 'un_sanctions'
    | 'ofac_sanctions'
    | 'adverse_media'
    | 'companies_house'
    | 'pep'
    | 'cpi';
  error: string;
  timestamp: string;
}

interface ReportData {
  adverseMedia?: {
    evidence?: AdverseMediaEvidenceItem[];
    filteredResults?: Array<{
      url?: string;
      title?: string;
      source?: string;
      filterReason?: string;
    }>;
    urls?: string[];
    hasFindings?: boolean;
    severity?: string;
  };
  evidenceFiles?: Array<{
    filename: string;
    url?: string;
  }>;
  screeningErrors?: ScreeningError[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// Extended assessment type with client details relation
type AssessmentWithDetails = RiskAssessment & {
  clientDetails?: Record<string, unknown>;
};

const RISK_LEVEL_STYLES: Record<RiskLevel, string> = {
  LOW: 'bg-green-100 text-green-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  HIGH: 'bg-red-100 text-red-800',
};

export function ReportPage({ assessmentId }: ReportPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: assessment, isLoading, error } = useAssessmentReport(assessmentId);
  const {
    data: screeningStatus,
    isLoading: isScreeningStatusLoading,
    error: screeningStatusError,
    refetch: refetchScreeningStatus,
  } = useScreeningStatus(assessmentId, true);
  const { data: balanceData, refetch: refetchBalance } = useCurrentBalance();
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [isDownloadingSraForm, setIsDownloadingSraForm] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);

  // Purchase flow state - tracks which step of the upgrade flow we're in
  type PurchaseStep = 'IDLE' | 'MATCH_REPORT' | 'EDD_UPSELL' | 'EDD' | 'WAITING_FOR_EDD';
  const [purchaseStep, setPurchaseStep] = useState<PurchaseStep>('IDLE');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  // Track whether EDD upgrade is pending (before PROCESSING status is received)
  // Used to show spinner in ScreeningModal before backend confirms processing started
  const [isEddPending, setIsEddPending] = useState(false);
  // Track whether we've seen PROCESSING status during EDD upgrade
  // This prevents stale COMPLETED status from triggering immediate completion
  const [hasSeenEddProcessing, setHasSeenEddProcessing] = useState(false);

  // Derived state for backwards compatibility
  const showPurchaseModal = purchaseStep !== 'IDLE' && purchaseStep !== 'WAITING_FOR_EDD';
  const isWaitingForEdd = purchaseStep === 'WAITING_FOR_EDD';

  // Track PROCESSING or GENERATING_REPORT status during EDD upgrade
  useEffect(() => {
    if (
      purchaseStep === 'WAITING_FOR_EDD' &&
      (screeningStatus?.status === 'PROCESSING' || screeningStatus?.status === 'GENERATING_REPORT')
    ) {
      setHasSeenEddProcessing(true);
      // Clear pending flag once we've confirmed EDD is processing
      if (isEddPending) {
        setIsEddPending(false);
      }
    }
  }, [purchaseStep, screeningStatus?.status, isEddPending]);

  // Reset the processing flag when not waiting for EDD
  useEffect(() => {
    if (purchaseStep !== 'WAITING_FOR_EDD') {
      setHasSeenEddProcessing(false);
      setIsEddPending(false);
    }
  }, [purchaseStep]);

  // Keep polling during EDD upgrade even if status is COMPLETED
  // The normal polling stops when COMPLETED, but we need to detect the EDD transition
  useEffect(() => {
    if (purchaseStep !== 'WAITING_FOR_EDD' || hasSeenEddProcessing) {
      return;
    }

    // Poll every 2 seconds until we see PROCESSING or GENERATING_REPORT
    const interval = setInterval(() => {
      refetchScreeningStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [purchaseStep, hasSeenEddProcessing, refetchScreeningStatus]);

  // Handle viewing report after EDD completion
  const handleViewEddReport = async () => {
    // Refetch the report data to get the updated report with EDD results
    // Note: useAssessmentReport uses wizardKeys.report, not wizardKeys.assessment
    await queryClient.refetchQueries({ queryKey: wizardKeys.report(assessmentId) });
    await queryClient.refetchQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
    // Dismiss the modal after data is refreshed
    setPurchaseStep('IDLE');
  };

  // Navigate to the dashboard, refreshing the lists first so an in-progress /
  // generating record shows immediately in the "In Progress Risk Assessments"
  // section without a manual refresh.
  const handleBackToDashboard = async () => {
    await queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });
    await navigate({ to: '/' });
  };

  // Check if user has paid for initial screening (basic report access)
  const hasPaidForScreening = screeningStatus?.paidServices?.initialScreening;

  // Check if user has purchased a full report (Match Report or EDD)
  const hasFullReportAccess =
    screeningStatus?.paidServices?.matchReport || screeningStatus?.paidServices?.eddReport;

  // SECURITY: Block access if screening status is loading, failed, or user hasn't paid for anything
  // This prevents users from seeing report content while payment status is being verified
  const shouldShowNoReportModal =
    !isLoading &&
    !isScreeningStatusLoading &&
    (!screeningStatus || screeningStatusError || (!hasPaidForScreening && !hasFullReportAccess)) &&
    !showPurchaseModal;

  // Show basic report if user has paid for screening but not full report
  // Note: We show BasicReportPage even when modals are open, so don't check showPurchaseModal
  const shouldShowBasicReport =
    !isLoading &&
    !isScreeningStatusLoading &&
    screeningStatus &&
    hasPaidForScreening &&
    !hasFullReportAccess;

  // A background enhancement (post-hoc EDD upgrade) is running on THIS record:
  // it has full-report access and its adverse-media job is still in flight. We
  // derive this from the record so the generating state is shown on fresh
  // navigation and survives a page refresh (not just the in-page purchase flow).
  const isEnhancing =
    purchaseStep === 'IDLE' &&
    !!hasFullReportAccess &&
    (screeningStatus?.status === 'PROCESSING' || screeningStatus?.status === 'GENERATING_REPORT');

  // When that enhancement finishes, refetch the report so the page swaps straight
  // from the generating overlay to the freshly enhanced report.
  const wasEnhancingRef = useRef(false);
  useEffect(() => {
    if (isEnhancing) {
      wasEnhancingRef.current = true;
    } else if (wasEnhancingRef.current && screeningStatus?.status === 'COMPLETED') {
      wasEnhancingRef.current = false;
      void queryClient.refetchQueries({ queryKey: wizardKeys.report(assessmentId) });
    }
  }, [isEnhancing, screeningStatus?.status, assessmentId, queryClient]);

  if (isLoading || isScreeningStatusLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-gray-50">
        <p className="text-lg text-gray-600">Failed to load report</p>
        <Button variant="outline" onClick={() => navigate({ to: '/' })}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const assessmentWithDetails = assessment as AssessmentWithDetails;
  const clientDetails = assessmentWithDetails.clientDetails;
  const reportData = (assessmentWithDetails.reportData as ReportData) || {};
  const riskLevel = (assessmentWithDetails.riskLevel as RiskLevel) || 'LOW';

  const handleDownloadSRAForm = async () => {
    if (isDownloadingSraForm) return;

    setIsDownloadingSraForm(true);
    try {
      await downloadSraForm(assessmentId);
      toast.success('SRA form downloaded successfully');
    } catch (error) {
      console.error('Failed to download SRA form:', error);
      toast.error('Failed to download SRA form. Please try again.');
    } finally {
      setIsDownloadingSraForm(false);
    }
  };

  const handleDownloadReport = async () => {
    if (isDownloadingReport) return;
    setIsDownloadingReport(true);
    try {
      await downloadFullReportPdf(assessmentId);
      toast.success('Report PDF downloaded successfully');
    } catch (err) {
      console.error('Failed to download report PDF:', err);
      toast.error('Failed to download report PDF. Please try again.');
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleDownloadEvidence = async (filename: string) => {
    try {
      await downloadEvidence(assessmentId, filename);
    } catch (error) {
      console.error('Failed to download evidence:', error);
    }
  };

  const handleDownloadAllEvidence = async () => {
    if (isDownloadingAll) return; // Prevent double-clicks

    setIsDownloadingAll(true);
    try {
      await downloadAllEvidence(assessmentId);
      toast.success('Evidence package downloaded successfully');
    } catch (error) {
      console.error('Failed to download all evidence:', error);
      toast.error('Failed to download evidence package. Please try again.');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  // Handle Match Report purchase - ALL users go here first
  const handleMatchReportPurchase = async () => {
    setIsPurchasing(true);
    try {
      // Deduct balance for Match Report
      const result = await deductBalance(SERVICES.MATCH_RISK_REPORT, assessmentId);

      // Record the paid service
      await apiClient.fetch(`/risk-assessments/${assessmentId}/record-paid-service`, {
        method: 'POST',
        body: JSON.stringify({
          serviceKey: SERVICES.MATCH_RISK_REPORT,
          transactionId: result.transactionId,
        }),
      });

      // Invalidate queries to reflect the purchase
      await queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
      await refetchBalance();

      // Show EDD upsell modal
      setPurchaseStep('EDD_UPSELL');
      toast.success('Match Report purchased');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Purchase failed');
    } finally {
      setIsPurchasing(false);
    }
  };

  // Handle EDD purchase (from upsell modal or direct)
  const handleEddPurchase = async () => {
    setIsPurchasing(true);
    try {
      // Deduct balance for EDD (charged against the current/source assessment).
      const result = await deductBalance(SERVICES.AI_CONFIDENCE_REPORT, assessmentId);

      // Mark EDD as pending before triggering upgrade
      // This ensures the modal shows spinner while waiting for PROCESSING status
      setIsEddPending(true);

      // Trigger EDD upgrade. This PRESERVES the original report and creates a
      // SEPARATE new dated record for the enhanced run; the server records the
      // EDD paid marker on the new record using the transaction id (so no
      // separate record-paid-service call is needed here).
      const upgrade = await upgradeToEdd(assessmentId, result.transactionId);

      await refetchBalance();

      // Invalidate the dashboard lists so the new IN_PROGRESS record shows in the
      // "In Progress Risk Assessments" section as soon as the user returns to the
      // dashboard — without a manual refresh. The global staleTime (5m) would
      // otherwise serve a cached list that omits the just-created record.
      await queryClient.invalidateQueries({ queryKey: riskAssessmentKeys.lists() });

      // The enhanced run lives on the NEW record — navigate there so the user
      // watches it generate, leaving the original intact in the dashboard.
      setPurchaseStep('IDLE');
      toast.success('Enhanced Due Diligence check started');
      await navigate({
        to: '/risk-assessment/$id/report',
        params: { id: upgrade.assessmentId },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Purchase failed');
      setIsEddPending(false);
    } finally {
      setIsPurchasing(false);
    }
  };

  // Skip EDD upsell - just show the Match Report
  const handleSkipEddUpsell = async () => {
    // Send report ready email since user has purchased the match report
    try {
      await apiClient.fetch(`/risk-assessments/${assessmentId}/send-report-email`, {
        method: 'POST',
      });
    } catch (emailError) {
      console.warn('Failed to send report ready email:', emailError);
    }

    // Refresh data and show report
    await queryClient.invalidateQueries({ queryKey: wizardKeys.screeningStatus(assessmentId) });
    await queryClient.invalidateQueries({ queryKey: wizardKeys.assessment(assessmentId) });
    await refetchScreeningStatus();

    setPurchaseStep('IDLE');
  };

  // Helper to render purchase modals - used in multiple places
  // Note: Uses isWaitingForEdd derived state to avoid TypeScript narrowing issues
  const renderPurchaseModals = (onSkipMatchReport: () => void, onSkipEdd: () => void) => (
    <>
      {/* Match Report Modal - ALL users see this first */}
      <MatchReportModal
        open={purchaseStep === 'MATCH_REPORT'}
        riskLevel={riskLevel}
        onPurchase={handleMatchReportPurchase}
        onSkip={onSkipMatchReport}
        onAddFunds={() => setShowAddFundsModal(true)}
        isPurchasing={isPurchasing}
      />

      {/* EDD Upsell Modal - shown after Match Report purchase */}
      <EddUpsellModal
        open={purchaseStep === 'EDD_UPSELL'}
        onPurchase={handleEddPurchase}
        onSkip={handleSkipEddUpsell}
        onAddFunds={() => setShowAddFundsModal(true)}
        isPurchasing={isPurchasing}
      />

      {/* EDD Modal - for direct EDD purchase (fallback) */}
      <EddModal
        open={purchaseStep === 'EDD'}
        onPurchase={handleEddPurchase}
        onSkip={onSkipEdd}
        onAddFunds={() => setShowAddFundsModal(true)}
        isPurchasing={isPurchasing}
      />

      {/* Screening Modal for EDD processing — shown for the in-page wait flow and
          for a record arrived-at mid-enhancement (post-hoc upgrade / refresh). */}
      <ScreeningModal
        open={isWaitingForEdd || isEnhancing}
        onOpenChange={() => undefined}
        isScreening={isEddPending}
        screeningStatus={screeningStatus}
        onViewReport={handleViewEddReport}
        onBackToDashboard={handleBackToDashboard}
      />

      {/* Add Funds Modal */}
      <AddFundsModal open={showAddFundsModal} onOpenChange={setShowAddFundsModal} />
    </>
  );

  // If user is in the middle of a purchase flow (EDD upsell or waiting for EDD),
  // show the modals regardless of report access status. This handles the case where
  // the user just purchased Match Report and we need to show the EDD upsell.
  if (purchaseStep === 'EDD_UPSELL' || purchaseStep === 'WAITING_FOR_EDD' || isEnhancing) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
        {/* Header */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white px-6">
          <div className="flex items-center gap-2">
            <Link to="/">
              <img src={logoImage} alt="OKYC" className="h-8" />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[14px] text-[#051923]">
              Balance: {balanceData?.balanceFormatted ?? '£0.00'}
            </span>
            <UserMenu />
          </div>
        </header>

        {/* Empty content area while showing modals */}
        <main className="flex-1" />

        {renderPurchaseModals(
          () => setPurchaseStep('IDLE'),
          () => setPurchaseStep('IDLE'),
        )}
      </div>
    );
  }

  // If user has paid for screening but not full report, show BasicReportPage
  if (shouldShowBasicReport) {
    return (
      <>
        <BasicReportPage
          assessmentId={assessmentId}
          onUpgrade={() => setPurchaseStep('MATCH_REPORT')}
        />
        {renderPurchaseModals(
          () => setPurchaseStep('IDLE'),
          () => setPurchaseStep('IDLE'),
        )}
      </>
    );
  }

  // If user hasn't purchased screening at all, show minimal page with modals only
  // This prevents malicious users from hiding the modal to see report content
  if (shouldShowNoReportModal) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
        {/* Header */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white px-6">
          <div className="flex items-center gap-2">
            <Link to="/">
              <img src={logoImage} alt="OKYC" className="h-8" />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[14px] text-[#051923]">
              Balance: {balanceData?.balanceFormatted ?? '£0.00'}
            </span>
            <UserMenu />
          </div>
        </header>

        {/* Empty content area - no report data exposed */}
        <main className="flex-1" />

        {/* No Report Access Modal - shown when not in purchase flow */}
        <NoReportAccessModal
          open={purchaseStep === 'IDLE'}
          onOpenChange={() => undefined}
          onGoBack={() => setPurchaseStep('MATCH_REPORT')}
          onReturnToDashboard={() => navigate({ to: '/' })}
        />

        {renderPurchaseModals(
          () => {
            setPurchaseStep('IDLE');
            navigate({ to: '/' });
          },
          () => {
            setPurchaseStep('IDLE');
            navigate({ to: '/' });
          },
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white px-6">
        <div className="flex items-center gap-2">
          <Link to="/">
            <img src={logoImage} alt="OKYC" className="h-8" />
          </Link>
          <button
            type="button"
            onClick={() => navigate({ to: '/' })}
            className="flex items-center text-gray-600 hover:text-gray-900"
            aria-label="Go back to dashboard"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[14px] text-[#051923]">
            Balance: {balanceData?.balanceFormatted ?? '£0.00'}
          </span>
          <UserMenu />
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-8 py-8">
          {/* Report Title & Risk Badge */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#051923]">Risk Assessment Report</h1>
            </div>
            <div
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-medium',
                RISK_LEVEL_STYLES[riskLevel],
              )}
            >
              {riskLevel} Risk
            </div>
          </div>

          {/* Client Details Section */}
          <ReportClientDetails clientDetails={clientDetails} assessment={assessmentWithDetails} />

          {/* Screening Log Section */}
          <ScreeningLogSection
            markdown={assessmentWithDetails.formattedReport}
            onOpenAuditTrail={() => setAuditTrailOpen(true)}
          />

          {/* Summary of Findings */}
          <SummaryOfFindingsSection
            summary={reportData.summary_of_findings as string | undefined}
          />

          {/* Business Risk Assessment - shown for Match Report and EDD */}
          {/* Note: Check both snake_case and camelCase as data may be stored either way */}
          <BusinessRiskSection
            matches={reportData.business_risk_matches || reportData.businessRiskMatches}
            hasHighRiskBusiness={
              reportData.has_high_risk_business ?? reportData.hasHighRiskBusiness
            }
            categories={reportData.business_risk_categories || reportData.businessRiskCategories}
          />

          {/* Screening Errors (if any) */}
          {reportData.screeningErrors && reportData.screeningErrors.length > 0 && (
            <section className="mb-8 rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <h2 className="text-lg font-semibold text-amber-800">Screening Warnings</h2>
              </div>
              <p className="mb-4 text-sm text-amber-700">
                Some screening checks encountered errors and may have incomplete results. Please
                review the following:
              </p>
              <ul className="space-y-2">
                {reportData.screeningErrors.map((err, index) => {
                  const checkLabels: Record<ScreeningError['check'], string> = {
                    uk_sanctions: 'UK Sanctions (OFSI)',
                    eu_sanctions: 'EU Sanctions',
                    un_sanctions: 'UN Sanctions',
                    ofac_sanctions: 'OFAC Sanctions',
                    adverse_media: 'Adverse Media',
                    companies_house: 'Companies House',
                    pep: 'PEP Check',
                    cpi: 'Corruption Index (CPI)',
                  };
                  return (
                    <li
                      key={`${err.check}-${index}`}
                      className="rounded bg-white p-3 text-sm shadow-sm"
                    >
                      <div className="font-medium text-amber-800">{checkLabels[err.check]}</div>
                      <div className="mt-1 text-gray-600">{err.error}</div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Evidence Package */}
          <EvidencePackageSection
            files={getEvidenceFiles(reportData)}
            onDownload={handleDownloadEvidence}
            onDownloadAll={handleDownloadAllEvidence}
            isDownloadingAll={isDownloadingAll}
          />

          {/* Adverse Media Findings / Sources */}
          <AdverseMediaSection
            evidence={reportData.adverseMedia?.evidence}
            filteredResults={reportData.adverseMedia?.filteredResults}
            urls={reportData.adverseMedia?.urls}
          />
        </div>
      </main>

      {/* Footer - Full Width */}
      <footer className="shrink-0 border-t bg-white px-4 py-4 sm:px-8">
        {/* Stacked full-width on mobile; from sm a right-aligned row that wraps
            rather than overflowing off the left edge. */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-6">
          {/* TODO: Re-enable when support feature is ready */}
          <button
            type="button"
            disabled
            className="text-center font-inter text-[16px] font-semibold text-[#131313] underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Get Support
          </button>
          <Button
            variant="outline"
            onClick={handleDownloadReport}
            disabled={isDownloadingReport || screeningStatus?.status !== 'COMPLETED'}
            className="w-full border-[#131313] text-[#131313] hover:bg-gray-50 sm:w-auto"
          >
            {isDownloadingReport ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </>
            )}
          </Button>
          {/* Upgrade to Enhanced button - shown for Standard tier users (paid for Match Report but not EDD) */}
          {screeningStatus?.paidServices?.matchReport &&
            !screeningStatus?.paidServices?.eddReport && (
              <Button
                onClick={() => setPurchaseStep('EDD')}
                variant="outline"
                className="w-full border-[#131313] text-[#131313] hover:bg-gray-50 sm:w-auto"
              >
                Upgrade to Enhanced
              </Button>
            )}
          {assessment.isLegalMatter && (
            <Button
              onClick={handleDownloadSRAForm}
              disabled={isDownloadingSraForm || assessment.screeningJobStatus !== 'COMPLETED'}
              className="w-full bg-[#131313] hover:bg-[#131313]/90 sm:w-auto"
            >
              {isDownloadingSraForm ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                'Download SRA Form'
              )}
            </Button>
          )}
        </div>
      </footer>

      {/* AI Audit Trail Modal */}
      <AuditTrailModal
        open={auditTrailOpen}
        onOpenChange={setAuditTrailOpen}
        assessmentId={assessmentId}
      />

      {/* Purchase modals for upgrade flow */}
      {renderPurchaseModals(
        () => setPurchaseStep('IDLE'),
        () => setPurchaseStep('IDLE'),
      )}
    </div>
  );
}
